import { styled } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Link,
} from '@mui/material';

const fontFamily = '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

export const BrainContainer = styled(Box)(({ theme }) => ({
  width: '270px',
  height: '270px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '48px',
  margin: '0 auto', 
  position: 'relative',
  left: '0',
  right: '0',
  marginTop: '48px', // Añadir este margen superior para compensar el header
  [theme.breakpoints.down('sm')]: {
    width: '200px',
    height: '200px',
    marginBottom: '10px',
  }
}));

export const CenteredContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 'calc(100% - 48px)', // Restar altura del TopBar
  padding: theme.spacing(4, 0, 0, 0),
  boxSizing: 'border-box',
  fontFamily,
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  textRendering: 'optimizeLegibility',
  backgroundColor: 'var(--bg-primary-elevated)',
  color: 'var(--label-primary)',
  overflowY: 'auto',
  overflowX: 'hidden',
  
  // Firefox - scrollbar inicialmente oculto
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  
  // Webkit scrollbar - inicialmente oculto
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    margin: '48px 0 0 0',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '10px',
    border: 'none',
    transition: 'background-color 0.3s ease',
  },
  '&::-webkit-scrollbar-button': {
    display: 'none',
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
  
  // Mostrar scrollbar solo al hacer hover
  '&:hover': {
    scrollbarColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.15) transparent'
      : 'rgba(0, 0, 0, 0.15) transparent',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.15)' 
      : 'rgba(0, 0, 0, 0.15)',
  },
  
  // Hover sobre el thumb mismo
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.25)' 
      : 'rgba(0, 0, 0, 0.25)',
  },
  
  // Active/dragging
  '&::-webkit-scrollbar-thumb:active': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.35)' 
      : 'rgba(0, 0, 0, 0.35)',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 0, 0, 0),
    paddingTop: 'calc(env(safe-area-inset-top) + 16px)',
    minHeight: '100%',
    
    '&::-webkit-scrollbar': {
      width: '6px',
    },
  }
}));

export const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily,
  flexGrow: 1,
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4), // Add horizontal padding here
  paddingRight: theme.spacing(4), // Add horizontal padding here
  [theme.breakpoints.down('sm')]: {
    padding: '16px 8px 0',
    maxWidth: '100%',
    boxSizing: 'border-box',
    gap: theme.spacing(2)
  }
}));

export const StyledFooter = styled(Box)(({ theme }) => ({
  width: '100vw', // Change to viewport width
  marginLeft: 'calc(-1 * (100vw - 100%) / 2)', // Center it by compensating for scrollbar
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(29, 29, 31, 0.72)' 
    : 'rgba(245, 245, 247, 0.72)',
  color: theme.palette.mode === 'dark' ? '#86868b' : '#6e6e73',
  padding: '24px 0',
  fontSize: '12px',
  letterSpacing: '-0.12px',
  lineHeight: '16px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#d2d2d7'}`,
  zIndex: 1000,
  marginTop: 'auto',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 0',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)'
  }
}));

export const LinksContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  justifyContent: 'center',
  flexWrap: 'wrap',
  flex: 1,
  padding: '0 16px', // Añadimos padding horizontal aquí para el contenido
});

export const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#86868b' : '#6e6e73',
  textDecoration: 'none',
  fontSize: '12px',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.mode === 'dark' ? '#f5f5f7' : '#1d1d1f',
  }
}));

export const HelpButton = styled(IconButton)({
  backgroundColor: 'transparent',
  color: 'var(--ui-accent)',
  padding: '8px',
  '[data-theme="light"] &': {
    color: '#0066CC',
  },
  '&:hover': {
    backgroundColor: 'var(--grouped-bg-primary-base)',
    color: 'var(--ui-accent)',
    '[data-theme="light"] &': {
      backgroundColor: '#F2F2F7',
      color: '#0066CC',
    },
  },
  '& svg': {
    stroke: 'var(--ui-accent)',
    strokeWidth: 1.5,
    '[data-theme="light"] &': {
      stroke: '#0066CC',
    },
  }
});